import React from 'react';

import { Link } from 'react-router-dom';

import DeleteIcon from '@mui/icons-material/Delete';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Box, Button, IconButton, Tooltip } from '@mui/material';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';

import { VoucherLog } from '~/services/PromoService';

import currencyFormatter from '~/utils/currencyFormatter';
import DateFormatterDetailed from '~/utils/dateFormatterDetails';

import CopyableUserLink from '../Common/CopyableUserLink';
import DebugModal from '../DebugModal/DebugModal';

export const voucherLogColumnSharedAttrs: Pick<
  GridColDef,
  'sortable' | 'display' | 'flex' | 'editable' | 'filterable'
> = {
  sortable: false,
  display: 'flex',
  flex: 1,
  filterable: false,
  editable: false,
};

type UserSummaryLite = { full_name?: string; email?: string };
type VoucherLite = { voucherName?: string };

export type BuildVoucherLogColumnsOptions = {
  usersSummaryMap: Map<string, UserSummaryLite>;
  vouchersMap: Map<string, VoucherLite>;
  onUserIdClick?: (userId: string) => void;
  onVoucherClick?: (voucherName: string) => void;
  onUnlog?: (log: VoucherLog) => void;
  unlogPending?: boolean;
  /** When false, omits the trailing unlog actions column (e.g. compact dialog grids). */
  includeUnlogColumn?: boolean;
};

export function buildVoucherLogColumns({
  usersSummaryMap,
  vouchersMap,
  onUserIdClick,
  onVoucherClick,
  onUnlog,
  unlogPending = false,
  includeUnlogColumn = true,
}: BuildVoucherLogColumnsOptions): Array<GridColDef<VoucherLog>> {
  const cols: Array<GridColDef<VoucherLog>> = [
    {
      field: 'fkOrder',
      headerName: 'Order ID',
      minWidth: 60,
      ...voucherLogColumnSharedAttrs,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', minWidth: 0 }}>
          <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{params.row.fkOrder}</Box>
          <Tooltip title="Open order">
            <IconButton
              component={Link}
              to={`/purchases/${params.row.fkOrder}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              size="small"
              sx={{ p: 0.25, ml: 0.5, flexShrink: 0 }}
            >
              <OpenInNewIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
    { field: 'currencyCode', headerName: 'Currency', minWidth: 120, flex: 0.5, ...voucherLogColumnSharedAttrs },
    {
      field: 'amount',
      headerName: 'Amount',
      minWidth: 130,
      flex: 0.5,
      ...voucherLogColumnSharedAttrs,
      renderCell: (params) => (
        <>
          {currencyFormatter(params.row.metadata?.voucherCurrency ?? params.row.currencyCode, params.row.amount)}{' '}
          {(params.row.metadata?.remainingAmountAfter ?? 0) > 0
            ? `(${params.row.metadata?.remainingAmountAfter} left)`
            : ''}
        </>
      ),
    },
    {
      field: 'voucherId',
      headerName: 'Voucher',
      minWidth: 150,
      ...voucherLogColumnSharedAttrs,
      renderCell: (params) => {
        const voucherId = params.row.voucherId;
        if (!voucherId) {
          return '-';
        }
        const voucher = vouchersMap.get(voucherId);
        const voucherNameForClick = voucher?.voucherName;
        const label = voucherNameForClick ?? voucherId;
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', minWidth: 0 }}>
            <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {onVoucherClick && voucherNameForClick ? (
                <Button
                  variant="text"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onVoucherClick(voucherNameForClick);
                  }}
                  sx={{ textTransform: 'none', p: 0, minWidth: 0 }}
                >
                  {label}
                </Button>
              ) : (
                label
              )}
            </Box>
            <Tooltip title="Open voucher">
              <IconButton
                component={Link}
                to={`/marketing/vouchers/${voucherId}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                size="small"
                sx={{ p: 0.25, ml: 0.5, flexShrink: 0 }}
              >
                <OpenInNewIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    },
    {
      field: 'userId',
      headerName: 'User',
      minWidth: 150,
      ...voucherLogColumnSharedAttrs,
      renderCell: (params) => {
        const uid = params.row.userId;
        if (!uid) {
          return '-';
        }
        const userName = usersSummaryMap.get(uid)?.full_name;
        if (onUserIdClick) {
          return (
            <Button variant="text" size="small" onClick={() => onUserIdClick(uid)} sx={{ textTransform: 'none' }}>
              {userName ?? uid}
            </Button>
          );
        }
        return <CopyableUserLink label={userName ?? uid} userId={uid} />;
      },
    },
    {
      field: 'createdAt',
      headerName: 'Created At',
      minWidth: 180,
      ...voucherLogColumnSharedAttrs,
      renderCell: (params) => DateFormatterDetailed({ date: params.value }),
    },
    {
      field: 'deletedAt',
      headerName: 'Deleted At',
      minWidth: 180,
      ...voucherLogColumnSharedAttrs,
      renderCell: (params) => (params.value ? DateFormatterDetailed({ date: params.value }) : '-'),
    },
    {
      field: 'createdBy',
      headerName: 'Created By',
      minWidth: 200,
      ...voucherLogColumnSharedAttrs,
      renderCell: (params) => {
        const userName = usersSummaryMap.get(params.row.createdBy)?.full_name;
        return <CopyableUserLink label={userName ?? params.row.createdBy} userId={params.row.createdBy} />;
      },
    },
    {
      field: 'debug',
      headerName: 'Debug',
      minWidth: 120,
      ...voucherLogColumnSharedAttrs,
      renderCell: (params) => <DebugModal type="generic" title="Log Row" data={params.row} />,
    },
    { field: 'voucherLogId', headerName: 'Log ID', flex: 1, ...voucherLogColumnSharedAttrs },
    { field: 'voucherUserId', headerName: 'Voucher User ID', flex: 1, ...voucherLogColumnSharedAttrs },
  ];

  if (includeUnlogColumn && onUnlog) {
    cols.push({
      field: 'actions',
      headerName: '',
      width: 90,
      ...voucherLogColumnSharedAttrs,
      flex: undefined,
      renderCell: (params: GridRenderCellParams<VoucherLog>) => {
        if (params.row.deletedAt) {
          return null;
        }
        return (
          <Tooltip title="Unlog voucher">
            <IconButton
              aria-label="Unlog voucher"
              size="small"
              onClick={() => onUnlog(params.row)}
              disabled={unlogPending}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        );
      },
    });
  }

  return cols;
}
